import { z } from "zod/v4";
import { defineResource, defineEndpoint } from "@visioni/a3s-client-api";
import { FEATURE_NAME } from "../constants";
import {
  NotificationCategory,
  NotificationCategoryPreferences,
  NotificationChannelPreferences,
  NotificationPreferenceScope,
} from "../schema";

/**
 * Client-API resource surface for self-service notification preferences.
 *
 * Tenant-scoped, not camera-scoped (`scope: "tenant"`) — a preference always belongs
 * to the calling user (`actor` from `requireAuth()`), never a path/body-supplied user
 * id, since this is self-service only (see DEL-660/DEL-715: a user can only ever
 * write their own preferences, there is no admin-manages-others flow).
 */

// #region List

export const NotificationPreferenceItem = z.object({
  scope: NotificationPreferenceScope,
  preferences: NotificationCategoryPreferences,
});
export type NotificationPreferenceItem = z.infer<typeof NotificationPreferenceItem>;

export const ListNotificationPreferencesResponse = z.object({
  preferences: z.array(NotificationPreferenceItem),
});
export type ListNotificationPreferencesResponse = z.infer<typeof ListNotificationPreferencesResponse>;

export const notificationPreferencesResource = defineResource({
  feature: FEATURE_NAME,
  resource: "notification-preferences",
  scope: "tenant",
});

/** GET every notification preference the calling user has saved (their "All Cameras" default, every site override, every camera override). */
export const listNotificationPreferencesEndpoint = defineEndpoint(notificationPreferencesResource, {
  responseSchema: ListNotificationPreferencesResponse,
});

// #endregion

// #region Set category

export const notificationPreferenceCategoryResource = defineResource({
  feature: FEATURE_NAME,
  resource: "notification-preferences/category",
  scope: "tenant",
});

export const SetNotificationPreferenceCategoryBody = z.object({
  scope: NotificationPreferenceScope,
  category: NotificationCategory,
  channels: NotificationChannelPreferences,
});
export type SetNotificationPreferenceCategoryBody = z.infer<typeof SetNotificationPreferenceCategoryBody>;

/**
 * PUT one scope+category's channel preferences for the calling user — a full
 * replace, not a merge (see `NotificationPreferencesService.setNotificationPreferenceCategory`).
 * Called once per toggle, immediately (autosave), not batched behind a "Save" button.
 */
export const setNotificationPreferenceCategoryEndpoint = defineEndpoint(notificationPreferenceCategoryResource, {
  method: "PUT",
  bodySchema: SetNotificationPreferenceCategoryBody,
  responseSchema: NotificationChannelPreferences,
});

// #endregion
